import React, { useState, useEffect, useCallback } from 'react';
import './KilobaCollection.css';
import { BaseLayout, KilobaCard } from '../components';
import { kilobaApi, KilobaCollectionEntry } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const KilobaCollection: React.FC = () => {
  const { user } = useAuth();
  const [collection, setCollection] = useState<KilobaCollectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedKiloba, setSelectedKiloba] = useState<{ type: string; code: string } | null>(null);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [selectedKilobaIndex, setSelectedKilobaIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const handleBackClick = () => {
    // Xử lý khi click nút back
    window.history.back();
  };

  // Handle kiloba card click
  const handleKilobaClick = (kilobaType: string, kilobaCode: string) => {
    const currentData = error ? fallbackData : collection;
    const index = currentData.findIndex(entry => 
      mapKilobaCode(entry.species.code) === kilobaCode
    );
    setSelectedKilobaIndex(index >= 0 ? index : 0);
    setSelectedKiloba({ type: kilobaType, code: kilobaCode });
    setShowPopup(true);
  };

  // Handle navigation to next/previous Kiloba
  const navigateToKiloba = useCallback((direction: 'next' | 'prev') => {
    const currentData = error ? fallbackData : collection;
    if (!currentData || currentData.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (selectedKilobaIndex + 1) % currentData.length;
    } else {
      newIndex = selectedKilobaIndex === 0 ? currentData.length - 1 : selectedKilobaIndex - 1;
    }
    
    const newKiloba = currentData[newIndex];
    if (newKiloba) {
      setSelectedKilobaIndex(newIndex);
      setSelectedKiloba({
        type: newKiloba.species.name,
        code: mapKilobaCode(newKiloba.species.code)
      });
    }
  }, [collection, error, selectedKilobaIndex]);

  // Touch event handlers for swipe detection
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(0); // Reset end position
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX) return;
    
    const distance = touchStartX - (touchEndX || touchStartX);
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      navigateToKiloba('next');
    } else if (isRightSwipe) {
      navigateToKiloba('prev');
    }
    
    // Reset touch positions
    setTouchStartX(0);
    setTouchEndX(0);
  };

  // Handle close popup
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedKiloba(null);
    setTouchStartX(0);
    setTouchEndX(0);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showPopup) return;
      
      if (event.key === 'ArrowLeft') {
        navigateToKiloba('prev');
      } else if (event.key === 'ArrowRight') {
        navigateToKiloba('next');
      } else if (event.key === 'Escape') {
        handleClosePopup();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPopup, navigateToKiloba]);

  // Additional touch event handling for better swipe detection
  useEffect(() => {
    if (!showPopup) return;

    let startX = 0;
    let startY = 0;

    const handleTouchStartGlobal = (e: Event) => {
      const touchEvent = e as TouchEvent;
      startX = touchEvent.touches[0].clientX;
      startY = touchEvent.touches[0].clientY;
    };

    const handleTouchEndGlobal = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!startX) return;

      const endX = touchEvent.changedTouches[0].clientX;
      const endY = touchEvent.changedTouches[0].clientY;
      const deltaX = startX - endX;
      const deltaY = startY - endY;

      // Check if it's a horizontal swipe (more horizontal than vertical)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        e.preventDefault();
        if (deltaX > 0) {
          navigateToKiloba('next'); // Swipe left -> next
        } else {
          navigateToKiloba('prev'); // Swipe right -> prev
        }
      }

      startX = 0;
      startY = 0;
    };

    const popupElement = document.querySelector('.kiloba-popup-container');
    if (popupElement) {
      popupElement.addEventListener('touchstart', handleTouchStartGlobal, { passive: false });
      popupElement.addEventListener('touchend', handleTouchEndGlobal, { passive: false });
    }

    return () => {
      if (popupElement) {
        popupElement.removeEventListener('touchstart', handleTouchStartGlobal);
        popupElement.removeEventListener('touchend', handleTouchEndGlobal);
      }
    };
  }, [showPopup, navigateToKiloba]);

  // Handle close warning popup
  const handleCloseWarningPopup = () => {
    setShowWarningPopup(false);
  };

  // Handle close success popup
  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  // Handle reward button click
  const handleRewardClick = () => {
    if (isRewardButtonEnabled) {
      // Show success popup when user has completed the collection
      setShowSuccessPopup(true);
      console.log('Claiming reward...');
    } else {
      // Show warning popup if not enough kilobas
      setShowWarningPopup(true);
    }
  };

  // Get popup image path
  const getPopupImagePath = (code: string): string => {
    const popupImages: { [key: string]: string } = {
      'kiloba_banker': '/images/kilohub/popup_kiloba_banker.png',
      'kiloba_conne_x': '/images/kilohub/popup_kiloba_conne_x.png',
      'kiloba_fin': '/images/kilohub/popup_kiloba_fin.png',
      'kiloba_toannang': '/images/kilohub/popup_kiloba_toannang.png',
      'kiloba_trust': '/images/kilohub/popup_kiloba_trust.png',
      'kiloba_viet': '/images/kilohub/popup_kiloba_viet.png'
    };
    
    return popupImages[code] || '/images/kilohub/popup_kiloba_banker.png'; // Default fallback
  };

  // Fetch kiloba collection data
  useEffect(() => {
    const fetchCollection = async () => {
      if (!user?.id) {
        setError('User not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await kilobaApi.getUserKilobaCollection(user.id);
        
        if (response.success && response.data) {
          setCollection(response.data);
        } else {
          setError('Failed to fetch kiloba collection');
        }
      } catch (error) {
        console.error('Error fetching kiloba collection:', error);
        setError('Failed to fetch kiloba collection');
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [user?.id]);

  // Map kiloba type code to display format
  const mapKilobaCode = (code: string): string => {
    const mapping: { [key: string]: string } = {
      'KILOBA_FIN': 'kiloba_fin',
      'KILOBA_TRUST': 'kiloba_trust', 
      'KILOBA_BANKER': 'kiloba_banker',
      'KILOBA_VIET': 'kiloba_viet',
      'KILOBA_CONNE_X': 'kiloba_conne_x',
    };
    return mapping[code] || code.toLowerCase();
  };

  // Fallback sample data if API fails
  const fallbackData: KilobaCollectionEntry[] = [
    { species: { id: 'fallback-1', name: 'Kiloba Banker', code: 'KILOBA_BANKER', createdAt: new Date().toISOString() }, quantity: 0, kilobas: [] },
    { species: { id: 'fallback-2', name: 'Kiloba Fin', code: 'KILOBA_FIN', createdAt: new Date().toISOString() }, quantity: 0, kilobas: [] },
    { species: { id: 'fallback-3', name: 'Kiloba Trust', code: 'KILOBA_TRUST', createdAt: new Date().toISOString() }, quantity: 0, kilobas: [] },
    { species: { id: 'fallback-4', name: 'Kiloba Toàn Năng', code: 'KILOBA_TOANNANG', createdAt: new Date().toISOString() }, quantity: 0, kilobas: [] },
    { species: { id: 'fallback-5', name: 'Kiloba Việt', code: 'KILOBA_VIET', createdAt: new Date().toISOString() }, quantity: 0, kilobas: [] }
  ];

  const displayData = error ? fallbackData : collection;

  // Check if user has at least 1 of each kiloba type
  const hasAllKilobaTypes = () => {
    // Ensure displayData is an array before using .every()
    if (!Array.isArray(displayData) || displayData.length === 0) {
      return false;
    }
    return displayData.every((entry: KilobaCollectionEntry) => entry.quantity > 0);
  };

  const isRewardButtonEnabled = hasAllKilobaTypes();

  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container" backgroundColor="transparent">
        <div className="kiloba-collection">
          <button className="kiloba-collection-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <div className="kiloba-collection-title">
            <span>Bộ sưu tập KILOBA</span>
          </div>
          <div className="kiloba-collection-content">
            {loading ? (
              <div className="loading-container">
                <p>Đang tải bộ sưu tập...</p>
              </div>
            ) : (
              <>
                {/* Grid layout cho kiloba cards */}
                <div className="kiloba-cards-container-collection">
                  {/* Hàng 1: 2 thẻ */}
                  <div className="kiloba-collection-row kiloba-collection-row-2">
                    {displayData[0] && (
                      <KilobaCard 
                        kilobaType={displayData[0].species.name}
                        kilobaCode={mapKilobaCode(displayData[0].species.code)}
                        quantity={displayData[0].quantity}
                        onClick={() => handleKilobaClick(displayData[0].species.name, mapKilobaCode(displayData[0].species.code))}
                      />
                    )}
                    {displayData[1] && (
                      <KilobaCard 
                        kilobaType={displayData[1].species.name}
                        kilobaCode={mapKilobaCode(displayData[1].species.code)}
                        quantity={displayData[1].quantity}
                        onClick={() => handleKilobaClick(displayData[1].species.name, mapKilobaCode(displayData[1].species.code))}
                      />
                    )}
                  </div>
                  
                  {/* Hàng 2: 3 thẻ */}
                  <div className="kiloba-collection-row kiloba-collection-row-3">
                    {displayData[2] && (
                      <KilobaCard 
                        kilobaType={displayData[2].species.name}
                        kilobaCode={mapKilobaCode(displayData[2].species.code)}
                        quantity={displayData[2].quantity}
                        onClick={() => handleKilobaClick(displayData[2].species.name, mapKilobaCode(displayData[2].species.code))}
                      />
                    )}

                    {displayData[3] && (
                      <KilobaCard 
                        kilobaType={displayData[3].species.name}
                        kilobaCode={mapKilobaCode(displayData[3].species.code)}
                        quantity={displayData[3].quantity}
                        onClick={() => handleKilobaClick(displayData[3].species.name, mapKilobaCode(displayData[3].species.code))}
                      />
                    )}
                    {displayData[4] && (
                      <KilobaCard 
                        kilobaType={displayData[4].species.name}
                        kilobaCode={mapKilobaCode(displayData[4].species.code)}
                        quantity={displayData[4].quantity}
                        onClick={() => handleKilobaClick(displayData[4].species.name, mapKilobaCode(displayData[4].species.code))}
                      />
                    )}
                  </div>

                  <div style={{padding: '8px', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex'}}>
                    <div style={{textAlign: 'center', justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 13, fontFamily: 'SVN-Gilroy', fontWeight: '700', wordWrap: 'break-word'}}>
                      10 giải thưởng đặc biệt hàng tháng
                    </div>
                    <div 
                      style={{
                        alignSelf: 'stretch', 
                        padding: 12, 
                        background: 'linear-gradient(135deg, #240CD7 0%, #7509D9 100%)', 
                        borderRadius: 8, 
                        outline: '1px #EA5C15 solid', 
                        outlineOffset: '-1px',
                        position: 'relative',
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        alignItems: 'center', 
                        gap: 16, 
                        display: 'flex'}}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        width: '40%',
                        opacity: 0.5,
                        overflow: 'hidden',
                        zIndex: 1,
                      }}>
                        <img src="/images/gift.png" style={{width: 300, height: 200, display: 'block'}} />
                      </div>
                      <div style={{textAlign: 'center', justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 13, fontFamily: 'SVN-Gilroy', fontWeight: '500', wordWrap: 'break-word'}}>
                        01 giải thưởng dành cho người hoàn thiện Bộ Sưu Tập nhanh nhất
                      </div>
                      <div style={{
                        alignSelf: 'stretch', 
                        textAlign: 'center', 
                        justifyContent: 'flex-end', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        color: 'white', 
                        fontSize: 16, 
                        fontFamily: 'SVN-Gilroy-Bold', 
                        fontWeight: '700', 
                        wordWrap: 'break-word',
                        zIndex: 2
                      }}>
                        Voucher GOT IT 2.000.000đ (0/1)
                      </div>
                      
                    </div>
                    <div style={{alignSelf: 'stretch', padding: 12, background: '#205AF4', borderRadius: 8, outline: '1px white solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'flex'}}>
                      <div style={{alignSelf: 'stretch', textAlign: 'center', justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 13, fontFamily: 'SVN-Gilroy', fontWeight: '500', wordWrap: 'break-word'}}>
                        09 giải thưởng dành cho người hoàn thiện Bộ Sưu Tập 
                      </div>
                      <div style={{
                        width: '90%',
                        outline: '1px white solid',
                      }}></div>
                      <div style={{alignSelf: 'stretch', textAlign: 'center', justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 16, fontFamily: 'SVN-Gilroy-Bold', fontWeight: '700', wordWrap: 'break-word'}}>
                        Voucher GOT IT 1.000.000đ (0/9)
                      </div>
                    </div>
                    <div data-property-1="Active">
                      <div 
                        className='reward-button-collection'
                        onClick={handleRewardClick}
                        >
                        Nhận thưởng
                      </div>
                    </div>
                  </div>
                  
                  
                  {/* <div className="reward-section-collection">
                    <div className="reward-promo-text-collection">
                      <p>5 người chơi thu thập đủ 5 KILOBA Toàn Năng sớm nhất mỗi tháng</p>
                      <p>sẽ nhận được <span className="voucher-highlight">Voucher GOT IT</span> trị giá 1.000.000đ</p>
                    </div>
                    <button 
                      className={`reward-button-collection ${!isRewardButtonEnabled ? 'disabled' : ''}`}
                      onClick={handleRewardClick}
                    >
                      Nhận thưởng
                    </button>
                  </div> */}
                </div>
              </>
            )}
            
            {/* Promotional text and reward button */}
            
            
            {/* Background caro pattern */}
            <div className="kiloba-collection-caro"></div>
          </div>
        </div>

        {/* Popup */}
        {showPopup && selectedKiloba && (
          <div className="kiloba-popup-overlay" onClick={handleClosePopup}>
            <div 
              className="kiloba-popup-container" 
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img src='/images/kilohub/icon_close.png' className="kiloba-popup-close" onClick={handleClosePopup} />
              
              {/* Previous button */}
              <button 
                className="kiloba-popup-nav kiloba-popup-nav-prev"
                onClick={() => navigateToKiloba('prev')}
                aria-label="Previous Kiloba"
              >
                &#8249;
              </button>
              
              {/* Next button */}
              <button 
                className="kiloba-popup-nav kiloba-popup-nav-next"
                onClick={() => navigateToKiloba('next')}
                aria-label="Next Kiloba"
              >
                &#8250;
              </button>
              
              <img 
                src={getPopupImagePath(selectedKiloba.code)}
                alt={`${selectedKiloba.type} details`}
                className="kiloba-popup-image"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ touchAction: 'none' }}
              />
              
              {/* Navigation indicators */}
              {/* <div className="kiloba-popup-indicators">
                {(error ? fallbackData : collection).map((_, index) => (
                  <div 
                    key={index}
                    className={`kiloba-popup-indicator ${index === selectedKilobaIndex ? 'active' : ''}`}
                    onClick={() => {
                      const currentData = error ? fallbackData : collection;
                      const kilobaData = currentData[index];
                      if (kilobaData) {
                        setSelectedKilobaIndex(index);
                        setSelectedKiloba({
                          type: kilobaData.species.name,
                          code: mapKilobaCode(kilobaData.species.code)
                        });
                      }
                    }}
                  />
                ))}
              </div> */}
              
              {/* Swipe hint */}
              <div className="kiloba-popup-hint">
                Vuốt trái/phải để xem các Kiloba khác
              </div>
            </div>
          </div>
        )}

        {/* Warning Popup */}
        {showWarningPopup && (
          <div className="kiloba-popup-overlay" onClick={handleCloseWarningPopup}>
            <div className="kiloba-warning-popup-container" onClick={(e) => e.stopPropagation()}>
              <img src='/images/kilohub/icon_close.png' className="kiloba-warning-popup-close" onClick={handleCloseWarningPopup} />
              <div className="kiloba-warning-content">
                <h3>Chưa đủ điều kiện nhận thưởng</h3>
                <p>Bạn cần sưu tập đủ mỗi loại KILOBA ít nhất 1 con để có thể nhận thưởng.</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="kiloba-popup-overlay" onClick={handleCloseSuccessPopup}>
            <div className="kiloba-warning-popup-container" onClick={(e) => e.stopPropagation()}>
              <img src='/images/kilohub/icon_close.png' className="kiloba-warning-popup-close" onClick={handleCloseSuccessPopup} />
              <div className="kiloba-warning-content">
                <h3>Chúc mừng bạn đã chinh phục thành công BST Kiloba!</h3>
                <p>Danh sách 10 Kiloba Master hoàn thành bộ sưu tập nhanh nhất kỳ tháng 10 và nhận được giải thưởng từ KienlongBank sẽ được công bố vào ngày 13/11/2025.</p>
                <p>Đừng bỏ lỡ!</p>
              </div>
            </div>
          </div>
        )}
      </BaseLayout>
    </div>
  );
};

export default KilobaCollection;
